/**
 * APC EstateFlow - Single Source of Truth Pricing Engine
 * 
 * IMPORTANT: This file is the ONLY source of pricing calculations.
 * All pricing displayed on the website MUST use these functions.
 * 
 * Last Updated: March 2026
 * Pricing Version: 2.1 (Jan 2026 +8% adjustment)
 */

export const PRICING_VERSION = '2.1';

/**
 * Calculate pricing for Landlord accounts
 * @param {number} units - Number of units
 * @param {number} buildings - Number of buildings
 * @returns {Object} Pricing breakdown
 */
export const calculateLandlordPricing = (units, buildings = 1) => {
  // Setup fee: $162 base + $81 per additional building
  const setupFee = 162 + ((buildings - 1) * 81);
  
  // Monthly fee based on unit count
  let monthlyFee;
  if (units <= 5) {
    monthlyFee = 161;
  } else if (units <= 10) {
    monthlyFee = 215;
  } else if (units <= 20) {
    monthlyFee = 323;
  } else if (units <= 50) {
    monthlyFee = 485;
  } else {
    monthlyFee = 755;
  }
  
  // Add $54/month per additional building for multi-location management
  if (buildings > 1) {
    monthlyFee += (buildings - 1) * 54;
  }
  
  const transactionFee = 5;
  
  return {
    setupFee,
    monthlyFee,
    transactionFee,
    firstMonthTotal: setupFee + monthlyFee,
    units,
    buildings,
    type: 'landlord',
    pricingVersion: PRICING_VERSION
  };
};

/**
 * Calculate pricing for HOA Community accounts
 * @param {number} units - Number of units
 * @param {number} buildings - Number of buildings
 * @returns {Object} Pricing breakdown
 */
export const calculateHOAPricing = (units, buildings = 1) => {
  // Setup fee: $323 base + $120 per additional building
  const setupFee = 323 + ((buildings - 1) * 120);
  
  // Monthly fee based on unit count
  let monthlyFee;
  if (units <= 10) {
    monthlyFee = 269;
  } else if (units <= 25) {
    monthlyFee = 431;
  } else if (units <= 50) {
    monthlyFee = 755;
  } else if (units <= 100) {
    monthlyFee = 1079;
  } else {
    monthlyFee = 1619;
  }
  
  // Add $65/month per additional building for multi-property management
  if (buildings > 1) {
    monthlyFee += (buildings - 1) * 65;
  }
  
  const transactionFee = 5;
  
  return {
    setupFee,
    monthlyFee,
    transactionFee,
    firstMonthTotal: setupFee + monthlyFee,
    units,
    buildings,
    type: 'hoa',
    pricingVersion: PRICING_VERSION
  };
};

/**
 * Calculate pricing for Vacation Rental accounts
 * @param {number} properties - Number of properties
 * @returns {Object} Pricing breakdown
 */
export const calculateVacationRentalPricing = (properties) => {
  // Flat setup fee
  const setupFee = 99;
  
  // Monthly fee based on property count
  let monthlyFee;
  if (properties <= 3) {
    monthlyFee = 19;
  } else if (properties <= 10) {
    monthlyFee = 69;
  } else if (properties <= 25) {
    monthlyFee = 199;
  } else if (properties <= 50) {
    monthlyFee = 599;
  } else {
    monthlyFee = 999; // 51+ Custom - contact sales
  }
  
  const transactionFee = 4; // Vacation rentals have 4% transaction fee
  
  return {
    setupFee,
    monthlyFee,
    transactionFee,
    firstMonthTotal: setupFee + monthlyFee,
    properties,
    type: 'vacation_rental',
    pricingVersion: PRICING_VERSION
  };
};

/**
 * Calculate pricing for Property Manager accounts
 * @returns {Object} Pricing breakdown
 */
export const calculatePropertyManagerPricing = () => {
  return {
    setupFee: 1499,
    monthlyFee: 215,
    transactionFee: 2,
    firstMonthTotal: 1499 + 215,
    type: 'property_manager',
    pricingVersion: PRICING_VERSION
  };
};

/**
 * Main pricing calculator - determines which pricing function to use
 * @param {string} accountType - Type of account (landlord, hoa, vacation_rental, property_manager)
 * @param {number} units - Number of units/properties
 * @param {number} buildings - Number of buildings (for landlord/hoa)
 * @returns {Object} Pricing breakdown
 */
export const calculatePricing = (accountType, units = 1, buildings = 1) => {
  const normalizedType = accountType?.toLowerCase()?.replace(/\s+/g, '_') || 'landlord';
  
  switch (normalizedType) {
    case 'landlord':
      return calculateLandlordPricing(units, buildings);
    
    case 'hoa':
    case 'hoa_community':
      return calculateHOAPricing(units, buildings);
    
    case 'vacation_rental':
    case 'vacation_rentals':
      return calculateVacationRentalPricing(units);
    
    case 'property_manager':
      return calculatePropertyManagerPricing();
    
    default:
      console.warn(`Unknown account type: ${accountType}, defaulting to landlord`);
      return calculateLandlordPricing(units, buildings);
  }
};

/**
 * Validate pricing matches expected values
 * Used as a guard before payment to ensure consistency
 * @param {Object} displayedPricing - Pricing shown to user
 * @param {string} accountType - Account type
 * @param {number} units - Number of units
 * @param {number} buildings - Number of buildings
 * @returns {Object} Validation result
 */
export const validatePricing = (displayedPricing, accountType, units, buildings) => {
  const calculatedPricing = calculatePricing(accountType, units, buildings);
  
  const setupFeeMatch = 
    (displayedPricing.setupFee || displayedPricing.setup_fee) === calculatedPricing.setupFee;
  const monthlyFeeMatch = 
    (displayedPricing.monthlyFee || displayedPricing.monthly_fee) === calculatedPricing.monthlyFee;
  
  const isValid = setupFeeMatch && monthlyFeeMatch;
  
  if (!isValid) {
    console.error('PRICING MISMATCH DETECTED!', {
      displayed: displayedPricing,
      calculated: calculatedPricing,
      accountType,
      units,
      buildings
    });
  }
  
  return {
    isValid,
    setupFeeMatch,
    monthlyFeeMatch,
    calculatedPricing,
    displayedPricing,
    mismatchDetails: !isValid ? {
      setupFeeDiff: (displayedPricing.setupFee || displayedPricing.setup_fee) - calculatedPricing.setupFee,
      monthlyFeeDiff: (displayedPricing.monthlyFee || displayedPricing.monthly_fee) - calculatedPricing.monthlyFee
    } : null
  };
};

/**
 * Format pricing for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount with $ and commas
 */
export const formatPrice = (amount) => {
  return `$${amount.toLocaleString()}`;
};

/**
 * Get pricing tier label based on units
 * @param {string} accountType 
 * @param {number} units 
 * @returns {string} Tier label
 */
export const getPricingTierLabel = (accountType, units) => {
  if (accountType === 'landlord') {
    if (units <= 5) return 'Starter';
    if (units <= 10) return 'Basic';
    if (units <= 20) return 'Standard';
    if (units <= 50) return 'Professional';
    return 'Enterprise';
  }
  if (accountType === 'hoa' || accountType === 'hoa_community') {
    if (units <= 10) return 'Small Community';
    if (units <= 25) return 'Medium Community';
    if (units <= 50) return 'Large Community';
    if (units <= 100) return 'Enterprise';
    return 'Enterprise Plus';
  }
  if (accountType === 'vacation_rental') {
    if (units <= 3) return 'Starter';
    if (units <= 10) return 'Growing';
    if (units <= 25) return 'Professional';
    if (units <= 50) return 'Business';
    return 'Enterprise';
  }
  return 'Custom';
};

export default {
  calculatePricing,
  calculateLandlordPricing,
  calculateHOAPricing,
  calculateVacationRentalPricing,
  calculatePropertyManagerPricing,
  validatePricing,
  formatPrice,
  getPricingTierLabel,
  PRICING_VERSION
};
